import stringFormatObj from 'string-format-obj';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import AppStoreModule from '@app/store/modules/AppStoreModule';
import SystemOfUnits from '../types/base/SystemOfUnits';
import GlobalsStoreModule from '../store/modules/GlobalsStoreModule';

const globalsStore: GlobalsStoreModule = getStoreModule(GlobalsStoreModule);
const appStore: AppStoreModule = getStoreModule(AppStoreModule);

const formatI18n = (tokenString: string, tokens: Record<string, any>): string => {
	if (!tokenString) return '';
	return stringFormatObj(tokenString, tokens);
};

const getLocale = (): string => (appStore.localeCode === 'de' ? 'de-CH' : appStore.localeCode);

export default {
	formatI18n,

	formatPrice(price: number): string {
		if (!price) {
			return '–';
		}

		return price.toLocaleString(getLocale(), { minimumFractionDigits: 2 }).replace(/00$/, '–');
	},
	formatPriceWithCurrency(price: number): string {
		if (!price) {
			return '–';
		}

		return `CHF ${price.toLocaleString(getLocale(), { minimumFractionDigits: 2 }).replace(/00$/, '–')}`;
	},
	formatElevation(elevation: number, withUnit: boolean = true): string {
		let translatedElevation: number = elevation;

		if (!translatedElevation) {
			return '–';
		}

		if (appStore.systemOfUnits === SystemOfUnits.Imperial) {
			translatedElevation = Math.round(translatedElevation * 3.28084);
		}

		if (!withUnit) {
			return translatedElevation.toLocaleString(getLocale());
		}

		return formatI18n(globalsStore.i18n.elevationAboveSeaLevel, {
			elevation: translatedElevation.toLocaleString(getLocale()),
			unitAboveSeaLevel:
				appStore.systemOfUnits === SystemOfUnits.Imperial
					? globalsStore.i18n.feetAboveSeaLevel
					: globalsStore.i18n.metersAboveSeaLevel
		});
	},
	formatElevationRange({ elevationMin, elevationMax }: { elevationMin: number; elevationMax: number }): string {
		let translatedElevationMin: number = elevationMin;
		let translatedElevationMax: number = elevationMax;

		if (appStore.systemOfUnits === SystemOfUnits.Imperial) {
			translatedElevationMin = Math.round(translatedElevationMin * 3.28084);
			translatedElevationMax = Math.round(translatedElevationMax * 3.28084);
		}

		return formatI18n(globalsStore.i18n.elevationRangeAboveSeaLevel, {
			elevationMin: translatedElevationMin.toLocaleString(getLocale()),
			elevationMax: translatedElevationMax.toLocaleString(getLocale()),
			unitAboveSeaLevel:
				appStore.systemOfUnits === SystemOfUnits.Imperial
					? globalsStore.i18n.feetAboveSeaLevel
					: globalsStore.i18n.metersAboveSeaLevel
		});
	},
	formatDistance(distance: number): string {
		let translatedDistance: number = distance;

		if (appStore.systemOfUnits === SystemOfUnits.Imperial) {
			translatedDistance = Math.round(translatedDistance * 0.62137);
		}

		return formatI18n(globalsStore.i18n.distanceWithUnit, {
			distance: translatedDistance.toLocaleString(getLocale()),
			distanceUnit:
				appStore.systemOfUnits === SystemOfUnits.Imperial
					? globalsStore.i18n.distanceUnitMiles
					: globalsStore.i18n.distanceUnitKilometers
		});
	},
	formatDate(datetime: number): string {
		const date: Date = new Date(datetime);
		return date.toLocaleDateString(getLocale());
	},
	formatDateTime(datetime: number): string {
		const date: Date = new Date(datetime);
		return `${date.toLocaleDateString(getLocale())} ${date.toLocaleTimeString(getLocale(), {
			hour: 'numeric',
			minute: '2-digit'
		})}`;
	},
	formatDuration(minutes: number): string {
		return formatI18n(globalsStore.i18n.durationWithUnit, {
			duration: minutes.toLocaleString(getLocale()),
			durationUnit: globalsStore.i18n.durationUnitMinutes
		});
	},
	nl2br(text: string): string {
		if (!text) {
			return text;
		}

		return text.replace(/\n/g, '<br>');
	}
};
